import { AlertCircle, CheckCircle2, ShoppingCart } from 'lucide-react';
import { memo, useCallback, useEffect, useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useAuth, useDialogStore } from '@/store';
import { formatNumberWithCommas } from '@/utils/FormatNumber';

type Props = {
  requestDone: boolean;
  handleSubmit: () => void;
  requestData?: {
    success: boolean;
    message: string;
    requestId: string;
    total: number;
    itemsCount: number;
    status?: 'Pending Approval' | 'Pending';
  };
  clientName?: string;
  error?: string;
  onViewRequest?: () => void;
  onErrorDialogClose?: () => void;
};

export const ConfirmationSection = memo(
  ({
    requestDone,
    handleSubmit,
    requestData,
    clientName = '',
    error = '',
    onViewRequest,
    onErrorDialogClose,
  }: Props) => {
    const { openRequestDialog } = useDialogStore();
    const { user: userData } = useAuth();
    const [successDialogOpen, setSuccessDialogOpen] = useState(requestDone);
    const [errorDialogOpen, setErrorDialogOpen] = useState(!!error);

    // Reset state when component dependencies change
    useEffect(() => {
      setSuccessDialogOpen(requestDone);
    }, [requestDone]);

    useEffect(() => {
      setErrorDialogOpen(!!error);
    }, [error]);

    // Reset dialog states when component unmounts
    useEffect(() => {
      return () => {
        setSuccessDialogOpen(false);
        setErrorDialogOpen(false);
      };
    }, []);

    // Handle view request functionality
    const handleViewRequest = useCallback(() => {
      if (requestData?.requestId) {
        // Reset dialog state first
        setSuccessDialogOpen(false);

        // Open the request dialog using Zustand
        openRequestDialog(requestData.requestId);

        // Call the parent callback if provided
        if (onViewRequest) {
          onViewRequest();
        }
      }
    }, [openRequestDialog, requestData?.requestId, onViewRequest]);

    // Handle retry functionality
    const handleRetry = useCallback(() => {
      setErrorDialogOpen(false);
      // Clear any existing error state to allow new errors to show
      if (onErrorDialogClose) {
        onErrorDialogClose();
      }
      // Small delay to ensure error state is cleared before retry
      setTimeout(() => {
        handleSubmit();
      }, 100);
    }, [handleSubmit, onErrorDialogClose]);

    // Handle success dialog close
    const handleSuccessDialogOpenChange = useCallback(
      (open: boolean) => {
        setSuccessDialogOpen(open);
        if (!open && onViewRequest) {
          // If dialog is closing and we have a view request handler, call it
          onViewRequest();
        }
      },
      [onViewRequest],
    );

    // Handle error dialog close
    const handleErrorDialogOpenChange = useCallback(
      (open: boolean) => {
        setErrorDialogOpen(open);
        if (!open && onErrorDialogClose) {
          onErrorDialogClose();
        }
      },
      [onErrorDialogClose],
    );

    // Avatar path construction
    const getAvatarSrc = useCallback((avatar?: string) => {
      return avatar
        ? `${import.meta.env.VITE_HEALTHY_EMPIRE_SERVER}/public/avatar/thumb/${avatar}`
        : '/images/no-user.jpg';
    }, []);

    if (requestDone) {
      return (
        <>
          <Button
            disabled
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:bg-gradient-to-r hover:from-green-700 hover:to-emerald-700 dark:from-green-500 dark:to-emerald-500"
          >
            <CheckCircle2 className="mr-2 h-5 w-5" />
            تم رفع الطلبية بنجاح
          </Button>

          <Dialog
            open={successDialogOpen}
            onOpenChange={handleSuccessDialogOpenChange}
          >
            <DialogContent
              className="border-2 border-green-100 p-0 shadow-lg dark:border-green-900/30"
              overlayClass="custom-dialog-overlay"
              aria-describedby="success-dialog-description"
            >
              <div className="bg-gradient-to-b from-green-50 to-background dark:from-green-900/10 dark:to-background">
                <DialogHeader className="bg-gradient-to-r from-green-50 via-green-100/50 to-green-50 px-6 pb-2 pt-6 dark:from-green-900/10 dark:via-green-900/20 dark:to-green-900/10">
                  <div className="mb-2 flex justify-center">
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white p-2 shadow-md dark:bg-black/40">
                      <div className="flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-green-500">
                        <CheckCircle2 className="h-10 w-10 text-white" />
                      </div>
                    </div>
                  </div>
                  <DialogTitle className="mb-1 text-center text-xl font-bold text-green-700 dark:text-green-400">
                    تم رفع الطلبية بنجاح
                    <DialogDescription className="text-center text-sm text-green-600 dark:text-green-500">
                      تم إنشاء الطلبية بنجاح وهي الآن قيد المعالجة
                    </DialogDescription>
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 px-6 pb-6 pt-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10 border-2 border-green-100 dark:border-green-800">
                        <AvatarImage
                          src={getAvatarSrc(userData?.avatar)}
                          alt={userData?.empName || 'المستخدم'}
                        />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {userData?.empName?.substring(0, 2) || 'NN'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h4 className="text-sm font-medium text-foreground">
                          {userData?.empName || 'المستخدم'}
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          {userData?.job?.jobName || 'الموظف المسؤول'}
                        </p>
                      </div>
                    </div>

                    <div className="flex-1 text-left">
                      <Badge
                        variant="outline"
                        className="border-green-100 bg-green-50 px-3 py-1 text-xs font-medium text-green-600 dark:border-green-900/30 dark:bg-green-900/20 dark:text-green-400"
                      >
                        {requestData?.status === 'Pending Approval'
                          ? 'بانتظار الموافقة'
                          : 'قيد الانتظار'}
                      </Badge>
                    </div>
                  </div>

                  <div className="flex flex-col items-center gap-3 rounded-lg border border-primary/10 bg-card p-4 shadow-sm">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-green-100 bg-white dark:border-green-900/50 dark:bg-black/40">
                      <div className="flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-primary/10 to-primary/20 text-xl font-bold text-primary">
                        {clientName
                          ? clientName.substring(0, 1).toUpperCase()
                          : '✓'}
                      </div>
                    </div>

                    <div className="text-center">
                      <h3 className="text-base font-semibold">{clientName}</h3>
                      <Badge
                        variant="outline"
                        className="mx-auto mt-1 border-primary/20 bg-primary/5 px-4 py-1 text-sm font-semibold text-primary"
                      >
                        رقم الطلبية: {requestData?.requestId}
                      </Badge>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="overflow-hidden rounded-lg border border-primary/10 bg-primary/5">
                      <div className="border-b border-primary/10 bg-primary/10 px-3 py-1 text-center">
                        <span className="text-xs font-medium text-primary">
                          إجمالي الأصناف
                        </span>
                      </div>
                      <div className="p-3 text-center">
                        <span className="text-xl font-bold text-primary">
                          {requestData?.itemsCount}
                        </span>
                      </div>
                    </div>

                    <div className="overflow-hidden rounded-lg border border-primary/10 bg-primary/5">
                      <div className="border-b border-primary/10 bg-primary/10 px-3 py-1 text-center">
                        <span className="text-xs font-medium text-primary">
                          إجمالي المبلغ
                        </span>
                      </div>
                      <div className="p-3 text-center">
                        <span className="text-xl font-bold text-primary">
                          {formatNumberWithCommas(requestData?.total || 0)} ج.م
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <DialogFooter className="border-t border-border/10 bg-muted/20 p-4">
                  <Button
                    onClick={handleViewRequest}
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 font-medium text-white hover:from-green-700 hover:to-emerald-700 dark:from-green-500 dark:to-emerald-500 dark:text-black dark:hover:from-green-600 dark:hover:to-emerald-600"
                  >
                    عرض تفاصيل الطلبية
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>
        </>
      );
    }

    return (
      <>
        <Button
          onClick={handleSubmit}
          size="lg"
          className={cn(
            'w-full gap-1.5 rounded-md bg-gradient-to-r from-green-600 to-emerald-600 font-semibold text-white transition-all',
            'hover:from-green-700 hover:to-emerald-700 hover:shadow-md',
            'dark:from-green-500 dark:to-emerald-500 dark:text-white dark:hover:from-green-600 dark:hover:to-emerald-600',
            'text-sm shadow-sm dark:text-black md:text-base',
          )}
        >
          <ShoppingCart className="h-4 w-4 md:h-5 md:w-5" />
          رفع طلبية الشراء
        </Button>

        <Dialog
          open={errorDialogOpen}
          onOpenChange={handleErrorDialogOpenChange}
        >
          <DialogContent
            className="border-2 border-red-100 p-0 shadow-md dark:border-red-900/30"
            overlayClass="custom-dialog-overlay"
            aria-describedby="error-dialog-description"
          >
            <div className="bg-gradient-to-b from-red-50 to-background dark:from-red-900/10 dark:to-background">
              <DialogHeader className="bg-gradient-to-r from-red-50 via-red-100/50 to-red-50 px-6 pb-2 pt-6 dark:from-red-900/10 dark:via-red-900/20 dark:to-red-900/10">
                <div className="mb-2 flex justify-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white p-2 shadow-md dark:bg-black/40">
                    <div className="flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-red-400 to-red-500">
                      <AlertCircle className="h-10 w-10 text-white" />
                    </div>
                  </div>
                </div>
                <DialogTitle className="mb-1 text-center text-xl font-bold text-red-700 dark:text-red-400">
                  حدث خطأ أثناء رفع الطلبية
                  <DialogDescription
                    className="text-center text-sm text-red-600 dark:text-red-500"
                    id="error-dialog-description"
                  >
                    {error || 'يرجى المحاولة مرة أخرى'}
                  </DialogDescription>
                </DialogTitle>
              </DialogHeader>

              <div className="p-4 md:p-6">
                <div className="rounded-lg border border-red-100 bg-red-50 p-4 dark:border-red-900/30 dark:bg-red-900/10">
                  <p className="text-sm text-red-700 dark:text-red-400">
                    {error ||
                      'حدث خطأ غير متوقع أثناء معالجة طلبك. يرجى المحاولة مرة أخرى.'}
                  </p>
                </div>
              </div>

              <DialogFooter className="border-t border-border/10 bg-muted/20 p-4">
                <div className="flex w-full flex-col gap-2 sm:flex-row">
                  <Button
                    variant="outline"
                    onClick={() => handleErrorDialogOpenChange(false)}
                    className="w-full border-red-200 bg-white text-red-700 hover:bg-red-50 hover:text-red-800 dark:border-red-500 dark:bg-red-950 dark:text-red-100 dark:hover:bg-red-900 dark:hover:text-red-50"
                  >
                    إغلاق
                  </Button>
                  <Button
                    onClick={handleRetry}
                    className="w-full bg-gradient-to-r from-red-600 to-red-500 font-medium text-white hover:from-red-700 hover:to-red-600 dark:from-red-500 dark:to-red-400 dark:text-black dark:hover:from-red-600 dark:hover:to-red-500"
                  >
                    إعادة المحاولة
                  </Button>
                </div>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  },
);

ConfirmationSection.displayName = 'ConfirmationSection';
